import { useTankSupply } from '@api/useTankSupply'
import { BestRackSelect } from '@components/Reports/TankSupplyDefaults/ApplyDefaultsDrawer/TankSupplySelect/BestRackSelect'
import { FilterHeader } from '@components/Reports/TankSupplyDefaults/ApplyDefaultsDrawer/TankSupplySelect/FilterHeader'
import { SupplyOptionSelector } from '@components/Reports/TankSupplyDefaults/ApplyDefaultsDrawer/TankSupplySelect/SupplyOptionSelector'
import { GraviButton, Horizontal, Texto, MomentDatePicker } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { Popover, Switch, Tooltip } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { formatters } from '@utils/intl/dateTimeFormatters'
const { RangePicker } = MomentDatePicker
const assignmentTypes = {
  SPECIFICSUPPLY: 'specific',
  BESTRACK: 'best-rack',
}

export function TankSupplySelect({ selectedRows, onDrawerSave }) {
  const [assignmentType, setAssignmentType] = useState(assignmentTypes.SPECIFICSUPPLY)
  const [filters, setFilters] = useState({})
  const [supplyPayload, setSupplyPayload] = useState()
  const [curveIds, setCurveIds] = useState([])
  const [bestTerminal, setBestTerminal] = useState()
  const [bestSupplyZone, setBestSupplyZone] = useState()
  const [isSaveLoading, setIsSaveLoading] = useState()
  const [generateVolume, setGenerateVolume] = useState(false)

  const { useTankSupplyOptionsQuery, useTankSupplyOptionsMetadataQuery } = useTankSupply()
  const { data: supplyOptions, status: queryStatus } = useTankSupplyOptionsQuery(supplyPayload)
  const { data: optionsMetadata } = useTankSupplyOptionsMetadataQuery()
  const queryClient = useQueryClient()
  function saveSupplyDefaults(dates) {
    setIsSaveLoading(true)
    const savePayload = {}
    // if dates exist we can assume it is in override
    if (dates) {
      savePayload.effective_from = dates[0].toISOString()
      savePayload.effective_to = dates[1].toISOString()
    }
    if (assignmentType === assignmentTypes.SPECIFICSUPPLY) {
      savePayload.store_tanks = selectedRows
      savePayload.curve_ids = curveIds
    } else {
      savePayload.store_tanks = selectedRows
      savePayload.terminal = bestTerminal?.children
      savePayload.supply_zone = bestSupplyZone?.children
    }
    savePayload.generate_volume = generateVolume
    const endPoint = dates ? api.assignTankSupplyOverride : api.assignTankSupply
    endPoint(savePayload)
      .then(() => {
        setIsSaveLoading(false)
        onDrawerSave()
        queryClient.invalidateQueries({
          queryKey: ['tankSupplyOptionsMetadata'],
        })
      })
      .catch((e) => {
        console.error(e)
      })
  }

  const bestRackOnSelect = (value, changeType) => {
    if (changeType === 'terminal') {
      setBestTerminal(value)
      setBestSupplyZone(null)
    } else {
      setBestSupplyZone(value)
      setBestTerminal(null)
    }
    setCurveIds(null)
  }

  useEffect(() => {
    if (selectedRows.length) {
      const terminal_ids = filters?.terminals?.map((t) => t.key) || []
      const supplier_ids = filters?.suppliers?.map((s) => s.key) || []
      const price_types = filters?.price_types || []
      const payload = selectedRows.map(({ store_number, tank_id }) => ({
        store_number,
        tank_id,
        terminal_ids,
        supplier_ids,
        price_types,
      }))
      setSupplyPayload(payload)
    }
  }, [selectedRows, filters])

  const canGenerateVolume = useMemo(() => {
    if (!curveIds?.length && !supplyOptions?.length) return false
    if (assignmentType === assignmentTypes.BESTRACK) return false

    const answer = supplyOptions?.find((option) =>
      option.components.find((component) => component.curve_id === curveIds[0])
    )?.can_generate_volume
    if (!answer) setGenerateVolume(false)
    return answer
  }, [curveIds, supplyOptions])

  return (
    <div className='h-100' style={{ boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.1)' }}>
      <div className='bg-2 vertical-flex flex-1'>
        <TypeSelect setAssignmentType={setAssignmentType} assignmentType={assignmentType} />

        {assignmentType === assignmentTypes.SPECIFICSUPPLY ? (
          <>
            <FilterHeader
              assignmentType={assignmentType}
              setFilters={setFilters}
              filters={filters}
              optionsMetadata={optionsMetadata}
            />
            <SupplyOptionSelector
              supplyOptions={supplyOptions}
              setCurveIds={setCurveIds}
              curveIds={curveIds}
              queryStatus={queryStatus}
            />
          </>
        ) : (
          <BestRackSelect
            optionsMetadata={optionsMetadata}
            bestTerminal={bestTerminal}
            bestSupplyZone={bestSupplyZone}
            bestRackOnSelect={bestRackOnSelect}
          />
        )}

        <TankSupplyOptionsFooter
          canSave={!curveIds?.length && !bestSupplyZone && !bestTerminal}
          saveSupplyDefaults={saveSupplyDefaults}
          isSaveLoading={isSaveLoading}
          canGenerateVolume={canGenerateVolume}
          setGenerateVolume={setGenerateVolume}
          generateVolume={generateVolume}
        />
      </div>
    </div>
  )
}

function TypeSelect({ assignmentType, setAssignmentType }) {
  return (
    <div className='flex items-center p-4 border-bottom'>
      <Texto category='h5' className='flex-1'>
        Assignment Type
      </Texto>
      <div>
        <GraviButton
          theme2={assignmentType === assignmentTypes.SPECIFICSUPPLY}
          onClick={() => setAssignmentType(assignmentTypes.SPECIFICSUPPLY)}
          className='mr-3'
          buttonText='Specific Supply'
        />
        <GraviButton
          onClick={() => setAssignmentType(assignmentTypes.BESTRACK)}
          success={assignmentType === assignmentTypes.BESTRACK}
          buttonText='Best Rack'
        />
      </div>
    </div>
  )
}

function TankSupplyOptionsFooter({
  saveSupplyDefaults,
  canSave,
  isSaveLoading,
  canGenerateVolume,
  setGenerateVolume,
  generateVolume,
}) {
  const [dates, setDates] = useState()
  const [isVisible, setIsVisible] = useState()
  const message = canGenerateVolume
    ? 'To compensate for liftings occurring outside of the system, liftings will be simulated for the daily demand of the tank if the tank has a carrier.'
    : 'Must select a contract option to generate lifting volume.'
  return (
    <div
      className='pb-3 items-center flex justify-end px-3'
      style={{ background: 'var(--theme-color-2-dim)', border: 'solid 1px var(--theme-color-2)', minHeight: 80 }}
    >
      <Horizontal className='mr-4'>
        <Texto category={'h6'} className='mr-1'>
          Generate Lifting Volume
        </Texto>
        <Popover
          title={<Texto category={'h6'}>{`Generate Lifting Volume is ${generateVolume ? 'On' : 'Off'}`}</Texto>}
          content={<Texto>{message}</Texto>}
        >
          <Switch
            disabled={!canGenerateVolume}
            onChange={(checked) => setGenerateVolume(checked)}
            checked={generateVolume}
          />
        </Popover>
      </Horizontal>
      <Popover
        content={() => (
          <div>
            <Texto className='mb-3'>Please select a time frame for your supply override.</Texto>
            <RangePicker
              style={{ width: '100%' }}
              className='mb-3'
              allowClear
              value={dates}
              onCalendarChange={(value) => setDates(value)}
              format={formatters.DATE}
            />
            <div className='bg-2 flex justify-end'>
              <GraviButton
                className='mr-3'
                buttonText='Cancel'
                onClick={() => {
                  setIsVisible(false)
                  setDates(null)
                }}
              />
              <GraviButton
                className='mr-3'
                buttonText='Save'
                success
                loading={isSaveLoading}
                disabled={!dates?.every((d) => !!d)}
                onClick={() => saveSupplyDefaults(dates)}
              />
            </div>
          </div>
        )}
        trigger='click'
        title='Temporary Override Effective'
        visible={isVisible}
        onOpenChange={() => setIsVisible(!isVisible)}
      >
        <GraviButton
          className='mr-3'
          buttonText='Set Temporary Override'
          theme2
          appearance='outlined'
          disabled={canSave}
        />
      </Popover>
      <GraviButton
        buttonText='Set Default Supply'
        disabled={canSave}
        loading={isSaveLoading}
        theme2
        onClick={() => saveSupplyDefaults()}
      />
    </div>
  )
}
